"use client";

import type React from "react";
import { useRef, useState, useEffect } from "react";
import { useInView } from "@/hooks/use-in-view";

const milestones = [
  { year: "1991", text: "MMI Opens as a Manufacturers Sales Agency" },
  { year: "1998", text: "Warehouse in USA" },
  { year: "1999", text: "Office in South Korea (Seoul)" },
  { year: "2003", text: "First office in China (Shanghai)" },
  { year: "2007", text: "First office in India (Pune)" },
  { year: "2010", text: "First Plant in India" },
  { year: "2011", text: "Second office in China (Jihan)" },
  { year: "2014", text: "Third office in China (Dongguan)" },
  { year: "2014", text: "Warehouse in China" },
  { year: "2015", text: "Office in Vietnam (Saigon)" },
  { year: "2017", text: "Second Plant in India" },
  { year: "2018", text: "Third Plant in India" },
  { year: "2019", text: "Second Supply office in Pune, India" },
  { year: "2023", text: "Office in Malaysia" },
  { year: "2024", text: "Wolverine Plant Expansion" },
  { year: "2024", text: "Casting Facility Joint Venture" },
  { year: "2025", text: "Global Sourcing offices" },
  { year: "2026 ( planned )", text: "Saigon Warehouse" },
  { year: "2026", text: "Global Sourcing office - Ahmedabad" },
];

export default function JourneyTimeline() {
  const { ref, inView } = useInView<HTMLDivElement>({ threshold: 0.1 });
  const scrollerRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLOListElement>(null);
  const railRef = useRef<HTMLDivElement>(null);

  const [isDown, setIsDown] = useState(false);
  const [startX, setStartX] = useState(0);
  const [scrollLeft, setScrollLeft] = useState(0);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);

  const updateScrollButtons = () => {
    const scroller = scrollerRef.current;
    if (!scroller) return;
    const { scrollLeft, scrollWidth, clientWidth } = scroller;
    setCanScrollLeft(scrollLeft > 10);
    setCanScrollRight(scrollLeft + clientWidth < scrollWidth - 10);
  };

  const sizeRailToContent = () => {
    const ol = listRef.current;
    const rail = railRef.current;
    if (!ol || !rail) return;
    rail.style.width = `${ol.scrollWidth}px`;
  };

  useEffect(() => {
    updateScrollButtons();
    sizeRailToContent();

    const scroller = scrollerRef.current;
    const ro = new ResizeObserver(() => {
      updateScrollButtons();
      sizeRailToContent();
    });

    if (scroller) scroller.addEventListener("scroll", updateScrollButtons);
    if (listRef.current) ro.observe(listRef.current);

    const onWinResize = () => sizeRailToContent();
    window.addEventListener("resize", onWinResize);

    return () => {
      if (scroller) scroller.removeEventListener("scroll", updateScrollButtons);
      ro.disconnect();
      window.removeEventListener("resize", onWinResize);
    };
  }, []);

  const onMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    const scroller = scrollerRef.current;
    if (!scroller) return;
    setIsDown(true);
    setStartX(e.pageX - scroller.offsetLeft);
    setScrollLeft(scroller.scrollLeft);
  };
  const onMouseLeave = () => setIsDown(false);
  const onMouseUp = () => setIsDown(false);
  const onMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    const scroller = scrollerRef.current;
    if (!isDown || !scroller) return;
    e.preventDefault();
    const x = e.pageX - scroller.offsetLeft;
    const walk = x - startX;
    scroller.scrollLeft = scrollLeft - walk;
  };

  const scrollByAmount = (delta: number) =>
    scrollerRef.current?.scrollBy({ left: delta, behavior: "smooth" });

  // Much smaller vertical spacing for mobile
  const OFFSET_UP = "mb-20 sm:mb-36";
  const OFFSET_DOWN = "mt-20 sm:mt-36";

  return (
    <section className="w-full bg-[#0F0F10] text-white py-6 sm:py-10 md:py-12 flex flex-col items-center">
      {/* Heading */}
      <div className="px-4 md:px-8 mb-2 mt-1">
        <h2 className="text-center text-[20px] sm:text-[28px] md:text-[40px]">
          Our Journey
        </h2>
        <div className="mx-auto mt-2 h-[2px] w-[50px] sm:w-[70px] rounded-full bg-white/85" />
      </div>

      {/* Timeline */}
      <div
        ref={ref}
        className="relative w-full flex items-center justify-center"
      >
        <div
          ref={scrollerRef}
          className="relative w-full overflow-x-auto [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden cursor-grab active:cursor-grabbing px-3 sm:px-6 py-4 sm:py-8 pb-8 sm:pb-14"
          onMouseDown={onMouseDown}
          onMouseLeave={onMouseLeave}
          onMouseUp={onMouseUp}
          onMouseMove={onMouseMove}
          aria-label="Company journey timeline"
        >
          <div className="relative w-max">
            {/* RAIL */}
            <div
              ref={railRef}
              className="absolute left-0 top-1/2 -translate-y-1/2 h-[1px] sm:h-[1.5px] bg-white/25 pointer-events-none"
            />

            <ol
              ref={listRef}
              className="relative grid auto-cols-[minmax(180px,260px)] sm:auto-cols-[minmax(260px,400px)] grid-flow-col gap-8 sm:gap-16 snap-x snap-mandatory"
            >
              {milestones.map((m, idx) => {
                const up = idx % 2 === 0;
                return (
                  <li key={idx} className="relative snap-start">
                    {/* Dot */}
                    <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-20 pointer-events-none">
                      <span className="block h-4 w-4 sm:h-6 sm:w-6 rounded-full bg-transparent shadow-[0_0_0_3px_rgba(253,195,0,0.10),0_0_0_8px_rgba(253,195,0,0.08)]" />
                      <span className="absolute inset-0 m-auto h-3 w-3 sm:h-4 sm:w-4 rounded-full ring-2 ring-[#FDC300]/45 bg-transparent" />
                      <span className="absolute inset-0 m-auto h-1.5 w-1.5 sm:h-2.5 sm:w-2.5 rounded-full bg-[#FDC300]" />
                    </div>

                    {/* Connector line */}
                    <div
                      className={`absolute left-1/2 ${
                        up
                          ? "bottom-1/2 translate-y-[2px] h-[25px] sm:h-[45px]"
                          : "top-1/2 -translate-y-[2px] h-[25px] sm:h-[45px]"
                      } w-[1.5px] bg-[#FDC300]`}
                    />

                    {/* Card */}
                    <div
                      className={`relative w-full max-w-[240px] sm:max-w-[400px] rounded-xl border border-[#FDC300]/70 bg-[#141414] p-3 sm:p-5 ${
                        up ? OFFSET_UP : OFFSET_DOWN
                      } transition-all duration-700 ${
                        inView
                          ? "opacity-100 translate-y-0"
                          : "opacity-0 translate-y-2"
                      }`}
                    >
                      <div className="text-[#FDC300] font-semibold text-[13px] sm:text-base">
                        {m.year}
                      </div>
                      <div className="mt-1 text-[11px] sm:text-[14px] leading-snug text-white/90">
                        {m.text}
                      </div>
                    </div>
                  </li>
                );
              })}
            </ol>
          </div>
        </div>

        {/* Arrows (desktop only) */}
        {canScrollLeft && (
          <button
            type="button"
            onClick={() => scrollByAmount(-480)}
            className="hidden md:flex absolute left-4 bottom-4 z-30 h-9 w-9 items-center justify-center rounded-full bg-[#FDC300] text-black text-lg font-bold shadow hover:brightness-95"
            aria-label="Scroll timeline left"
          >
            ‹
          </button>
        )}
        {canScrollRight && (
          <button
            type="button"
            onClick={() => scrollByAmount(480)}
            className="hidden md:flex absolute right-4 bottom-4 z-30 h-9 w-9 items-center justify-center rounded-full bg-[#FDC300] text-black text-lg font-bold shadow hover:brightness-95"
            aria-label="Scroll timeline right"
          >
            ›
          </button>
        )}
      </div>
    </section>
  );
}
